import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { line, radius, shadow, font, transition } from 'features/dashboard/tokens'
import CloseRoundedIcon         from '@mui/icons-material/CloseRounded'
import WavingHandRoundedIcon    from '@mui/icons-material/WavingHandRounded'
import LibraryBooksRoundedIcon  from '@mui/icons-material/LibraryBooksRounded'
import AutoAwesomeRoundedIcon   from '@mui/icons-material/AutoAwesomeRounded'
import ArrowForwardRoundedIcon  from '@mui/icons-material/ArrowForwardRounded'

const STORAGE_KEY = 'bp_dashboard_onboarded_v1'

export const shouldShowOnboarding = () => {
  try { return window.localStorage.getItem(STORAGE_KEY) !== '1' }
  catch { return false }
}

export const markOnboardingDone = () => {
  try { window.localStorage.setItem(STORAGE_KEY, '1') } catch {}
}

const SLIDES = [
  {
    Icon: AutoAwesomeRoundedIcon,
    eyebrow: 'New',
    title: 'Compliance, AI powered',
    body: 'Building Passport is the centralised document store for your building which enables our real estate-expert AI, Guru, to understand your building digitally and match relevant legislative requirements automatically.',
  },
  {
    Icon: LibraryBooksRoundedIcon,
    eyebrow: 'Everything in one place',
    title: 'Familiar interface. Guided set-up.',
    body: 'As long as your documents are synced (via SharePoint etc.), Guru will help you keep on top of your compliance, tasks, contracts and contractors in a familiar view with a guided set-up and ongoing support.',
  },
  {
    Icon: WavingHandRoundedIcon,
    eyebrow: 'AI assist',
    title: 'Guru suggests, you decide',
    body: 'Guru automatically suggests what needs to be (or what has been) done as documents are uploaded. Agree, customise or dismiss with one click and maintain a real-time report view across your portfolio, buildings and units.',
  },
]

/**
 * Three-slide explainer shown to brand-new users on first dashboard load.
 * Ends with a CTA to start the guided fix wizard with their top priorities
 * pre-queued. Persists dismissal in localStorage so it never reappears.
 */
const OnboardingWizard = ({ open, onClose, onStartGuidedFix }) => {
  const [idx, setIdx] = useState(0)

  if (!open) return null

  const slide = SLIDES[idx]
  const SlideIcon = slide.Icon
  const isLast = idx === SLIDES.length - 1

  const close = () => { markOnboardingDone(); onClose() }
  const next = () => {
    if (isLast) {
      close()
      onStartGuidedFix?.()
    } else {
      setIdx(idx + 1)
    }
  }

  return (
    <Backdrop>
      <Card>
        <CloseBtn onClick={close}><CloseRoundedIcon style={{ fontSize: 18 }} /></CloseBtn>

        <Progress>
          {SLIDES.map((_, i) => (
            <ProgressDot key={i} $on={i <= idx} />
          ))}
        </Progress>

        <Eyebrow>{slide.eyebrow}</Eyebrow>

        <Icon>
          <SlideIcon style={{ fontSize: 56, color: '#33becc' }} />
        </Icon>

        <Title>{slide.title}</Title>
        <Body>{slide.body}</Body>

        <Actions>
          <Skip onClick={close}>Skip intro</Skip>
          <Primary onClick={next}>
            {isLast ? (
              <>Start guided fix <ArrowForwardRoundedIcon style={{ fontSize: 16 }} /></>
            ) : (
              <>Next <ArrowForwardRoundedIcon style={{ fontSize: 16 }} /></>
            )}
          </Primary>
        </Actions>
      </Card>
    </Backdrop>
  )
}

export default OnboardingWizard

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.55);
  backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  z-index: 1500; padding: 24px;
`

const Card = styled.div`
  position: relative;
  width: 100%; max-width: 480px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${radius.md};
  padding: 36px 36px 28px;
  box-shadow: ${shadow.overlay};
  text-align: center;
  animation: ${slideIn} 320ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
`

const CloseBtn = styled.button`
  position: absolute; top: 14px; right: 14px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  background: rgba(12,18,41,0.06);
  border: none; border-radius: ${radius.round};
  cursor: pointer; color: ${({ theme }) => theme.colors.haiti};
  &:hover { background: rgba(12,18,41,0.14); }
`

const Progress = styled.div`
  display: inline-flex; gap: 6px;
  margin-bottom: 18px;
`

const ProgressDot = styled.span`
  width: 28px; height: 4px;
  border-radius: ${radius.xs};
  background: ${({ $on }) => $on ? '#33becc' : line.base};
  transition: ${transition};
`

const Eyebrow = styled.div`
  font-size: ${font.caption};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 800;
  margin-bottom: 12px;
`

const Icon = styled.div`
  display: inline-flex; align-items: center; justify-content: center;
  width: 88px; height: 88px;
  background: rgba(51, 190, 204, 0.10);
  border: 1px solid rgba(51, 190, 204, 0.25);
  border-radius: ${radius.round};
  margin-bottom: 18px;
`

const Title = styled.h2`
  margin: 0 0 10px;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  letter-spacing: -0.6px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.2;
`

const Body = styled.p`
  margin: 0 0 24px;
  font-size: ${font.body};
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.grey[800]};
  max-width: 36ch;
  margin-left: auto;
  margin-right: auto;
`

const Actions = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
`

const Skip = styled.button`
  font: inherit;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  &:hover { color: ${({ theme }) => theme.colors.haiti}; }
`

const Primary = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 12px 22px;
  background: ${({ theme }) => theme.colors.haiti};
  color: #fff;
  border: none; border-radius: ${radius.pill};
  font-size: ${font.body};
  font-weight: 800;
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: ${shadow.lift};
  &:hover { filter: brightness(1.10); transform: translateY(-1px); }
`
